import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { createBillingAddress } from "@store/actions/billingAddresses";
import { my } from "@store/actions/user";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { clearCart, closeCart, setCheckoutStep, setPaymentInFlight } from "@store/reducers/cart";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ERoutes } from "@utils/constants";
import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import { computeSubscriptionPricing } from "@/utils/pricing";
import { getStripe } from "@/utils/stripe";

import {
  BackBtn,
  ButtonGroup,
  FormRow,
  IconCircle,
  InputField,
  InputGroup,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  SlidePanelFooter,
  StepHeaderWrap,
  StepSubtitle,
  StepTitle,
} from "./styles";
import { CA_PROVINCES, COUNTRIES, COUNTRY_CODES, US_STATES } from "./types";


const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  /* Never let the flex button compress the fixed-size circle into an oval —
     on the narrow mobile button the spinner + text overflow and flex-shrink
     would squish it into a "horseshoe". Mirrors BtnSpinner's flex: 0 0 auto. */
  flex: 0 0 auto;
  box-sizing: border-box;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: -2px;
`;

const DropdownWrap = styled.div`position: relative; width: 100%;`;
const DropdownTrigger = styled.div<{ $open: boolean; $error: boolean; $hasValue: boolean }>`
  width: 100%; padding: 14px 36px 14px 16px;
  background: rgba(255, 255, 255, 0.12); border: 1px solid ${({ $error }) => ($error ? "#e47a6a" : "rgba(255, 255, 255, 0.2)")};
  border-radius: 8px; color: ${({ $hasValue }) => ($hasValue ? "#ffffff" : "rgba(255, 255, 255, 0.8)")};
  font-family: "Raleway", sans-serif; font-size: 0.9375rem; cursor: pointer; box-sizing: border-box; position: relative;
  &::after { content: ""; position: absolute; right: 14px; top: 50%; transform: translateY(-50%) ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")}; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid rgba(255, 255, 255, 0.5); transition: transform 0.2s; }
`;
const DropdownMenu = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background: #EDE0D1;
  border-radius: 12px;
  z-index: 1400;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 320px;
`;
const CountrySearchSection = styled.div`
  padding: 8px;
  background: #EDE0D1;
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
`;

const CountrySearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #fff;
  background: #9b7f67;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;
const DropdownList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 0 8px;
  min-height: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #8B7460; border-radius: 3px; }
`;
const DropdownItem = styled.div<{ $highlighted?: boolean }>`
  padding: 9px 16px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #8B7460;
  cursor: pointer;
  transition: all 0.15s;
  background: ${({ $highlighted }) => ($highlighted ? "rgba(89, 65, 46, 0.12)" : "transparent")};
  ${({ $highlighted }) => ($highlighted ? "color: #3a2616;" : "")}

  &:hover {
    background: rgba(89, 65, 46, 0.08);
    color: #3a2616;
  }
`;

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

/* PHONE-ONLY fixed header (modal-template-phone.md): centred title, no icon,
   symmetric ✕ spacing (min-height 68, side-only padding). Hidden ≥601px so
   desktop/tablet keep the centred in-body icon-circle header + subtitle. */
const PhoneHeader = styled.div.attrs({ "data-slide-header": "" })`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-height: 68px;
    padding: 0 24px;

    h2 {
      font-family: "Cormorant Garamond", serif;
      font-size: 1.5rem;
      font-weight: 400;
      color: #fff;
      margin: 0;
      line-height: 1.2;
      text-align: center;
    }
  }
`;

/* Scroll body — trims its top padding on phone so the first section hugs the
   fixed header (the header's adjacency rule can't reach it through the
   ScrollArea wrapper, so the trim lives here). */
const Body = styled(SlidePanelBody)`
  @media (max-width: 600px) {
    padding-top: 0;
  }
`;

/* The original centred icon-circle header — desktop/tablet only. */
const DesktopHeader = styled(StepHeaderWrap)`
  @media (max-width: 600px) {
    display: none;
  }
`;

/* First section label hugs the fixed header on phone. */
const FirstSectionLabel = styled(SectionLabel)`
  @media (max-width: 600px) {
    margin-top: 2px;
  }
`;

/* Footer: Back + Subscribe. The label is one line ("Subscribe $19.00/mo"), so
   Back shrinks to its content and Subscribe takes the rest — the same
   treatment as the upgrade payment footer. A 50/50 split was fine for the old
   two-line label but leaves the one-line label wrapping on a phone. */
const Actions = styled(ButtonGroup)`
  margin-top: 0;
  border-top: none;
  padding-top: 0;

  ${BackBtn} {
    flex: 0 0 auto;
    width: auto;
    padding-left: 20px;
    padding-right: 20px;
  }
  ${PrimaryBtn} {
    flex: 1;
    width: auto;
    white-space: nowrap;
  }
`;

/* The amount is part of the label itself — "Subscribe ($29.00/mo)" — one element,
   one weight, one colour. It was a separate lighter span until 2026-07-25, which
   read as two competing pieces; and two stacked lines before 2026-07-21, which
   sat off-balance against the single-line glyph. */

/* Scroll affordance — iOS doesn't render the custom WebKit scrollbar for touch
   overflow, so a tall body can cut off at a clean line that reads as "the end".
   A bottom fade + a gently bouncing chevron appear only while there's more to
   scroll (cleared the moment you reach the bottom), signalling "more below"
   regardless of scrollbar visibility. */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const scrollHint = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
`;

const BottomFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 52px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  /* Fades into the modal's bottom background colour so content appears to
     continue under the edge. Ramps to full opacity (stronger than a soft
     wash) without growing taller. */
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.8) 52%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

/* "Scroll ⌄" hint sitting on the fade — a small label left of the chevron,
   both bouncing together to read as a scroll cue. */
const ScrollHint = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ebc28e;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  animation: ${scrollHint} 1.4s ease-in-out infinite;

  span {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ScrollChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface BillingFields {
  firstName: string; lastName: string; email: string;
  address: string; addressLine2: string;
  city: string; zipCode: string; country: string;
  // State / province — surfaced as a dropdown only for US / Canada
  // (where Stripe AVS depends on it). For other countries this stays
  // empty and is omitted from the Stripe call.
  stateRegion: string;
  // Business pair — both required together (B2B invoice) or both empty (B2C).
  // §14a UStG: B2B invoices need company + VAT id; B2C neither.
  companyName: string;
  vatId: string;
}
const REQUIRED_FIELDS: (keyof BillingFields)[] = ["firstName", "lastName", "address", "city", "zipCode", "country"];
const STATE_REQUIRED_COUNTRIES = new Set(["United States", "Canada"]);

const SubPaymentForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { subscription, isSwitchToAnnualMode, isReactivationMode, termsAccepted, withdrawalWaiverAccepted } = useAppSelector((state) => state.cart);
  const subConfig = useSubscriptionConfig();
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;

  const [paying, setPaying] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Payment errors render at the bottom of the scroll body; auto-scroll them
  // into view so the user actually sees them (the action button is in the
  // fixed footer, so an error below the fold would otherwise be missed).
  const errorRef = useRef<HTMLDivElement>(null);
  // Idempotency key for the create call: generated per ATTEMPT and reused only
  // while that attempt is in flight, so a re-click after a lost response can't
  // create a second Stripe subscription. It is cleared on every failure (see
  // `failAttempt`) — a key must not outlive its attempt. Holding it across a
  // declined card broke retrying outright: Stripe rejects a reused key whose
  // parameters changed ("can only be used with the same parameters"), and the
  // parameters DO change, because the failed attempt leaves a PAST_DUE row that
  // becomes the account's "current" subscription and so alters
  // `previousSubscriptionId`. The user got a bare 500 on every retry.
  const idemKeyRef = useRef<string>("");
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [error]);
  const [ready, setReady] = useState(false);
  // `complete` fires once the user has fully picked / filled a payment
  // method. Stays false during Link's "Use this card" half-step before OTP.
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof BillingFields, boolean>>>({});
  // Per-field shake — only the inputs that failed validation wobble,
  // not the whole address block. Reset 500ms after the shake fires so
  // the animation can re-trigger on the next failed submit.
  const [shakeFields, setShakeFields] = useState<Partial<Record<keyof BillingFields, boolean>>>({});

  const [billing, setBilling] = useState<BillingFields>({
    firstName: (user as any)?.user?.firstName || "",
    lastName: "",
    email: (user as any)?.user?.email || "",
    address: "", addressLine2: "", city: "", zipCode: "", country: "",
    stateRegion: "",
    companyName: "", vatId: "",
  });

  const hasVat = !!billing.vatId.trim();
  const hasCompany = !!billing.companyName.trim();
  const vatCompanyMismatch = (hasVat && !hasCompany) || (!hasVat && hasCompany);

  const [billingLoaded, setBillingLoaded] = useState(false);

  // Scroll affordance: track whether the body still has content below the fold
  // so the bottom fade + chevron only show while there's more to scroll.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    update();
    el.addEventListener("scroll", update, { passive: true });
    // Content height changes (PaymentElement mounting, error box, dropdowns) +
    // viewport resizes both move the scroll boundary — recompute on each.
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    const mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", schedule);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || billingLoaded) return;
    setBillingLoaded(true);
    instance.get("/billing-addresses").then((resp) => {
      const addresses = resp.data || [];
      const defaultAddr = addresses.find((a: any) => a.isDefault) || addresses[0];
      if (defaultAddr) {
        setBilling((prev) => ({
          firstName: defaultAddr.firstName || prev.firstName,
          lastName: defaultAddr.lastName || prev.lastName,
          email: defaultAddr.email || prev.email || (user as any)?.user?.email || "",
          address: defaultAddr.street || prev.address,
          addressLine2: defaultAddr.addressLine2 || prev.addressLine2,
          city: defaultAddr.city || prev.city,
          zipCode: defaultAddr.zipCode || prev.zipCode,
          country: defaultAddr.country || prev.country,
          stateRegion: defaultAddr.stateRegion || prev.stateRegion,
          companyName: defaultAddr.companyName || prev.companyName,
          vatId: defaultAddr.vatId || prev.vatId,
        }));
      }
    }).catch(() => {});
  }, [isAuthenticated, billingLoaded]);

  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryHighlight, setCountryHighlight] = useState(-1);
  const [countryRect, setCountryRect] = useState({ top: 0, left: 0, width: 0 });
  const [stateOpen, setStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [stateHighlight, setStateHighlight] = useState(-1);
  const [stateRect, setStateRect] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const countrySearchRef = useRef<HTMLInputElement>(null);
  const stateSearchRef = useRef<HTMLInputElement>(null);

  // Reposition portal-rendered dropdowns relative to their triggers.
  const repositionCountry = useCallback(() => {
    const r = dropdownRef.current?.getBoundingClientRect();
    if (r) setCountryRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);
  const repositionState = useCallback(() => {
    const r = stateDropdownRef.current?.getBoundingClientRect();
    if (r) setStateRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);
  useLayoutEffect(() => { if (countryOpen) repositionCountry(); }, [countryOpen, repositionCountry]);
  useLayoutEffect(() => { if (stateOpen) repositionState(); }, [stateOpen, repositionState]);
  useEffect(() => {
    if (!countryOpen && !stateOpen) return;
    const handler = () => { if (countryOpen) repositionCountry(); if (stateOpen) repositionState(); };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [countryOpen, stateOpen, repositionCountry, repositionState]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-cart-dropdown-portal="1"]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) setCountryOpen(false);
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(target)) setStateOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = COUNTRIES.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase()));

  const updateField = (field: keyof BillingFields, value: string) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const { channels, claims, billingPeriod } = subscription;
  const isAnnual = billingPeriod === "annual";
  const pricing = computeSubscriptionPricing(subConfig, { channels, claims, isAnnual });
  const premiumBase = pricing.baseMonthly;
  const extraChannels = pricing.extraChannels;
  const extraClaimPacks = pricing.extraClaimPacks;
  const addonsPrice = pricing.addonsMonthly; // claims billed per channel
  const monthlyTotal = premiumBase + addonsPrice;

  /** End a failed attempt: show why, unlock the button, and retire the
   *  idempotency key so the next click is a genuinely new attempt. */
  const failAttempt = useCallback((message: string) => {
    setError(message);
    setPaying(false);
    setFinalizing(false);
    dispatch(setPaymentInFlight(false));
    idemKeyRef.current = "";
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || paying || finalizing) return;

    // Validate billing
    const errors: Partial<Record<keyof BillingFields, boolean>> = {};
    REQUIRED_FIELDS.forEach((f) => { if (!billing[f].trim()) errors[f] = true; });
    // State / province is required for US + CA (Stripe AVS depends on it).
    if (STATE_REQUIRED_COUNTRIES.has(billing.country) && !billing.stateRegion.trim()) {
      errors.stateRegion = true;
    }
    // §14a UStG mutual dependency: company + VAT must both be filled or
    // both be empty. Flag the offending field of the pair specifically.
    if (vatCompanyMismatch) {
      if (!hasCompany) errors.companyName = true;
      if (!hasVat) errors.vatId = true;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Shake only the fields that failed validation.
      setShakeFields(errors);
      setTimeout(() => setShakeFields({}), 500);
      return;
    }

    setPaying(true);
    setError(null);
    // Lock the cart shut for the duration — the ✕/Escape must not abandon a
    // charge in flight (Back is already disabled below).
    dispatch(setPaymentInFlight(true));

    try {
      // Switch-to-annual mode: existing monthly user → annual upgrade.
      // Backend creates a new annual Stripe sub with metadata flagging
      // the old monthly sub for cancellation; old sub is cancelled
      // automatically by the webhook after this payment succeeds.
      // No billing address needed (using existing). Consent IS re-recorded:
      // monthly -> annual is a new contract at a new price with its own 14-day
      // period, so the signup waiver does not carry over. It is captured on
      // SubReviewView — this flow starts at plan-config and passes through it,
      // so there is no need (and no excuse) to ask a second time here.
      // Per-attempt idempotency key so a network retry of THIS submit can't
      // create two Stripe subscriptions (+ two DB rows).
      if (!idemKeyRef.current) idemKeyRef.current = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      const idempotencyKey = idemKeyRef.current;
      const resp = isSwitchToAnnualMode
        ? await instance.post("/stripe/subscriptions/switch-to-annual", {
            idempotencyKey,
            termsAccepted,
            waiverAccepted: withdrawalWaiverAccepted,
          })
        : await instance.post("/stripe/subscriptions/create", {
            idempotencyKey,
            billingPeriod,
            channels,
            claims,
            reactivation: isReactivationMode,
            billingAddress: {
              firstName: billing.firstName,
              lastName: billing.lastName,
              email: billing.email || (user as any)?.user?.email || "",
              street: billing.address,
              addressLine2: billing.addressLine2 || undefined,
              city: billing.city,
              stateRegion: billing.stateRegion || undefined,
              zipCode: billing.zipCode,
              country: billing.country,
              companyName: billing.companyName || undefined,
              vatId: billing.vatId || undefined,
            },
            // The values the user actually ticked on SubReviewView, not a
            // hardcoded `true`. Recorded server-side in consent_records.
            termsAccepted,
            waiverAccepted: withdrawalWaiverAccepted,
          });

      const { clientSecret, subscriptionId } = resp.data;

      // Deferred-mode (Elements options.mode = "subscription") requires an
      // explicit elements.submit() before confirmPayment, and clientSecret
      // must be passed through to confirmPayment for it to know which intent
      // to confirm.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        failAttempt(submitError.message || "Payment details invalid. Please review and try again.");
        return;
      }

      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${ERoutes.AccountSubscription}`,
          payment_method_data: {
            billing_details: {
              name: `${billing.firstName} ${billing.lastName}`.trim(),
              email: billing.email || (user as any)?.user?.email || "",
              // PaymentElement is mounted with fields.billingDetails="never",
              // so Stripe requires every billing_details field to be passed
              // here — including phone, even when we don't collect one.
              phone: "",
              address: {
                line1: billing.address,
                line2: billing.addressLine2 || undefined,
                city: billing.city,
                // Send ISO state code for US/CA (Stripe AVS uses it). For
                // other countries we still send an empty string because
                // billingDetails="never" requires the field to be present.
                state: billing.stateRegion || "",
                postal_code: billing.zipCode,
                country: COUNTRY_CODES[billing.country] || "",
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        failAttempt(result.error.message || "Payment failed. Please try again.");
      } else {
        // Stay in the cart with the Pay button locked into "Finalizing
        // order…" while we wait for the webhook to write the order row.
        // Eliminates the intermediate /order/success polling page —
        // once the order is ready we jump straight to its detail URL.
        setFinalizing(true);

        // Save the billing address in parallel — its result doesn't gate the
        // navigation, and the webhook order creation is on its own clock.
        const saveBillingPromise: Promise<void> = isAuthenticated
          ? dispatch(createBillingAddress({
              firstName: billing.firstName,
              lastName: billing.lastName,
              street: billing.address,
              addressLine2: billing.addressLine2 || undefined,
              city: billing.city,
              stateRegion: billing.stateRegion || undefined,
              zipCode: billing.zipCode,
              country: billing.country,
              companyName: billing.companyName || undefined,
              vatId: billing.vatId || undefined,
              // Business pair filled → invoice should reflect B2B record.
              customerType: hasCompany && hasVat ? "business" : "private",
              isDefault: true,
            }) as any).then(() => undefined).catch(() => undefined)
          : Promise.resolve();

        // Poll for the order created by the invoice.paid webhook. Tight
        // 600ms interval so we navigate within ~1 poll of the webhook
        // finishing. 30s ceiling matches the old OrderSuccess timeout —
        // if Stripe is genuinely slow we fall back to the by-stripeSub
        // polling page so the user still lands somewhere sensible.
        // Returns the full order object once the webhook has written it,
        // not just the id — so we can hand it through navigation state
        // and OrderSuccess can render immediately without re-fetching
        // (and without flashing a "Loading order..." state).
        const pollOrder = async (): Promise<any | null> => {
          const started = Date.now();
          while (Date.now() - started < 30000) {
            try {
              const pollResp = await instance.get(
                `/orders/by-stripe-sub/${encodeURIComponent(subscriptionId)}?type=subscription`,
              );
              if (pollResp.data && pollResp.data.id) return pollResp.data;
            } catch { /* not ready yet */ }
            await new Promise((r) => setTimeout(r, 600));
          }
          return null;
        };

        const [orderData] = await Promise.all([pollOrder(), saveBillingPromise]);

        // Refresh the Redux user object so the header / mega menu / account
        // type context all flip from "Free / Upgrade now" to the active
        // Subscription state before the user lands on the success page.
        // The webhook updated `users.plan` in the DB; the SPA cache is stale
        // until we refetch.
        try { await dispatch(my() as any); } catch { /* non-blocking */ }

        // Payment settled and the order exists — release the close lock before
        // we shut the cart, so the flag can't outlive the attempt.
        dispatch(setPaymentInFlight(false));
        dispatch(clearCart());
        dispatch(closeCart());
        if (orderData && orderData.id) {
          // Hand the order object through navigation state so OrderSuccess
          // skips the fetch + "Loading order..." flash and renders the
          // moment it mounts.
          navigate(`${ERoutes.OrderSuccess}/${orderData.id}`, { state: { order: orderData } });
        } else {
          // Webhook genuinely slow — fall back to the by-stripeSub URL
          // which keeps polling on the OrderSuccess page itself.
          navigate(`${ERoutes.OrderSuccess}?stripeSub=${encodeURIComponent(subscriptionId)}&type=subscription`);
        }
      }
    } catch (err: any) {
      failAttempt(err?.response?.data?.message || "We couldn't complete the payment. Please check your card details and try again.");
    }
  // termsAccepted + withdrawalWaiverAccepted MUST be listed. This handler is
  // a useCallback with an explicit dep list, so omitting them makes the closure
  // capture their initial `false` — which is exactly what happened: a
  // switch-to-annual completed the purchase and posted waiverAccepted:false,
  // writing no consent row at all, while the UI had both boxes ticked.
  }, [stripe, elements, paying, finalizing, billing, subscription, dispatch, navigate, isAuthenticated, user, hasCompany, hasVat, vatCompanyMismatch, isSwitchToAnnualMode, isReactivationMode, termsAccepted, withdrawalWaiverAccepted, failAttempt]);

  // Stable PaymentElement options — a fresh object each render makes
  // react-stripe-js re-apply options to the element, which drops focus from a
  // card field mid-tap (e.g. when the keyboard opening flips the scroll
  // affordance and re-renders this form). Memoise so it's applied once.
  const paymentElementOptions = useMemo(() => ({
    layout: "tabs",
    business: { name: "Audio Sanctum" },
    fields: { billingDetails: "never" },
    wallets: { applePay: "auto", googlePay: "auto", link: "auto" },
  }) as any, []);

  return (
    <>
      {/* PHONE: fixed header — centred title, no icon (✕ stays top-right). */}
      <PhoneHeader>
        <h2>Payment</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
      {/* DESKTOP/TABLET: original centred icon-circle header (hidden on phone). */}
      <DesktopHeader>
        <IconCircle><CreditCardIcon /></IconCircle>
        <StepTitle>Payment</StepTitle>
        <StepSubtitle>{isAnnual ? "Annual" : "Monthly"} Subscription</StepSubtitle>
      </DesktopHeader>

      <>
        <FirstSectionLabel><span>Billing Address</span></FirstSectionLabel>
        <FormRow>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.firstName}>
              <InputField type="text" placeholder="First name" value={billing.firstName} onChange={(e) => updateField("firstName", e.target.value)} $error={!!fieldErrors.firstName} />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.lastName}>
              <InputField type="text" placeholder="Last name" value={billing.lastName} onChange={(e) => updateField("lastName", e.target.value)} $error={!!fieldErrors.lastName} />
            </ShakeWrap>
          </InputGroup>
        </FormRow>
        <InputGroup style={{ marginTop: "8px" }}>
          <ShakeWrap $shake={!!shakeFields.address}>
            <InputField type="text" placeholder="Street address" value={billing.address} onChange={(e) => updateField("address", e.target.value)} $error={!!fieldErrors.address} />
          </ShakeWrap>
        </InputGroup>
        <InputGroup style={{ marginTop: "8px" }}>
          <InputField type="text" placeholder="Apartment, suite, etc. (optional)" value={billing.addressLine2} onChange={(e) => updateField("addressLine2", e.target.value)} />
        </InputGroup>
        <FormRow style={{ marginTop: "8px" }}>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.city}>
              <InputField type="text" placeholder="City" value={billing.city} onChange={(e) => updateField("city", e.target.value)} $error={!!fieldErrors.city} />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.zipCode}>
              <InputField type="text" placeholder="ZIP / Postal code" value={billing.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} $error={!!fieldErrors.zipCode} />
            </ShakeWrap>
          </InputGroup>
        </FormRow>
        <InputGroup style={{ marginTop: "8px", marginBottom: STATE_REQUIRED_COUNTRIES.has(billing.country) ? "8px" : "20px" }}>
          <ShakeWrap $shake={!!shakeFields.country}>
          <DropdownWrap ref={dropdownRef}>
            <DropdownTrigger
              $open={countryOpen}
              $error={!!fieldErrors.country}
              $hasValue={!!billing.country}
              onClick={() => {
                setCountryOpen(!countryOpen);
                setCountrySearch("");
                setCountryHighlight(-1);
                setTimeout(() => countrySearchRef.current?.focus(), 50);
              }}
            >
              {billing.country || "Country"}
            </DropdownTrigger>
            {countryOpen && createPortal(
              <DropdownMenu
                data-cart-dropdown-portal="1"
                $top={countryRect.top}
                $left={countryRect.left}
                $width={countryRect.width}
              >
                <CountrySearchSection>
                  <CountrySearchInput
                    ref={countrySearchRef}
                    type="text"
                    placeholder="Search..."
                    value={countrySearch}
                    onChange={(e) => { setCountrySearch(e.target.value); setCountryHighlight(0); }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setCountryHighlight((prev) => Math.min(prev + 1, filteredCountries.length - 1));
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setCountryHighlight((prev) => Math.max(prev - 1, 0));
                      } else if (e.key === "Enter" && countryHighlight >= 0 && countryHighlight < filteredCountries.length) {
                        e.preventDefault();
                        const picked = filteredCountries[countryHighlight];
                        updateField("country", picked);
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                        if (!STATE_REQUIRED_COUNTRIES.has(picked)) {
                          setBilling((prev) => ({ ...prev, stateRegion: "" }));
                        }
                      } else if (e.key === "Escape") {
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                      }
                    }}
                    autoFocus
                  />
                </CountrySearchSection>
                <DropdownList>
                  {filteredCountries.map((c, i) => (
                    <DropdownItem
                      key={c}
                      $highlighted={i === countryHighlight}
                      onClick={() => {
                        updateField("country", c);
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                        // Clear any stale state when switching countries —
                        // a CA province in a US field would fail Stripe AVS.
                        if (!STATE_REQUIRED_COUNTRIES.has(c)) {
                          setBilling((prev) => ({ ...prev, stateRegion: "" }));
                        }
                      }}
                    >{c}</DropdownItem>
                  ))}
                </DropdownList>
              </DropdownMenu>,
              document.body,
            )}
          </DropdownWrap>
          </ShakeWrap>
        </InputGroup>

        {/* State / province dropdown — only for US + CA. Uses the same
            custom DropdownWrap/Trigger/List/Item as Country so the
            visual treatment (caret, focus, hover) is identical. */}
        {STATE_REQUIRED_COUNTRIES.has(billing.country) && (() => {
          const list = billing.country === "Canada" ? CA_PROVINCES : US_STATES;
          const filtered = list.filter((s) => s.name.toLowerCase().includes(stateSearch.toLowerCase()));
          const selected = list.find((s) => s.code === billing.stateRegion);
          const placeholder = billing.country === "Canada" ? "Province" : "State";
          return (
            <InputGroup style={{ marginBottom: "20px" }}>
              <ShakeWrap $shake={!!shakeFields.stateRegion}>
              <DropdownWrap ref={stateDropdownRef}>
                <DropdownTrigger
                  $open={stateOpen}
                  $error={!!fieldErrors.stateRegion}
                  $hasValue={!!billing.stateRegion}
                  onClick={() => {
                    setStateOpen(!stateOpen);
                    setStateSearch("");
                    setStateHighlight(-1);
                    setTimeout(() => stateSearchRef.current?.focus(), 50);
                  }}
                >
                  {selected?.name || placeholder}
                </DropdownTrigger>
                {stateOpen && createPortal(
                  <DropdownMenu
                    data-cart-dropdown-portal="1"
                    $top={stateRect.top}
                    $left={stateRect.left}
                    $width={stateRect.width}
                  >
                    <CountrySearchSection>
                      <CountrySearchInput
                        ref={stateSearchRef}
                        type="text"
                        placeholder="Search..."
                        value={stateSearch}
                        onChange={(e) => { setStateSearch(e.target.value); setStateHighlight(0); }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setStateHighlight((prev) => Math.min(prev + 1, filtered.length - 1));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setStateHighlight((prev) => Math.max(prev - 1, 0));
                          } else if (e.key === "Enter" && stateHighlight >= 0 && stateHighlight < filtered.length) {
                            e.preventDefault();
                            updateField("stateRegion", filtered[stateHighlight].code);
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          } else if (e.key === "Escape") {
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          }
                        }}
                        autoFocus
                      />
                    </CountrySearchSection>
                    <DropdownList>
                      {filtered.map((s, i) => (
                        <DropdownItem
                          key={s.code}
                          $highlighted={i === stateHighlight}
                          onClick={() => {
                            updateField("stateRegion", s.code);
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          }}
                        >
                          {s.name}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  </DropdownMenu>,
                  document.body,
                )}
              </DropdownWrap>
              </ShakeWrap>
            </InputGroup>
          );
        })()}

        {/* Business pair — VAT + Company must both be filled or both empty.
            §14a UStG: B2B invoices need both, B2C neither. Per-field shake
            now lives directly on each input so only the offender(s) wobble
            on a mismatched submission. */}
        <FormRow style={{ marginTop: "8px" }}>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.companyName}>
              <InputField
                type="text"
                value={billing.companyName}
                onChange={(e) => updateField("companyName", e.target.value)}
                placeholder="Company (optional)"
                $error={!!fieldErrors.companyName || (vatCompanyMismatch && !hasCompany)}
              />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.vatId}>
              <InputField
                type="text"
                value={billing.vatId}
                onChange={(e) => updateField("vatId", e.target.value)}
                placeholder="VAT (optional)"
                $error={!!fieldErrors.vatId || (vatCompanyMismatch && !hasVat)}
              />
            </ShakeWrap>
          </InputGroup>
        </FormRow>
      </>

      <SectionLabel><span>Payment Method</span></SectionLabel>
      <div style={{ marginBottom: "20px" }}>
        <PaymentElement
          onReady={() => setReady(true)}
          onChange={(e: any) => {
            setPaymentComplete(!!e?.complete);
            // Touching the payment details starts a new attempt, so the previous
            // attempt's message ("your card was declined") no longer applies —
            // leaving it up while the user types a different card reads as a
            // verdict on the card they are entering now.
            setError(null);
          }}
          options={paymentElementOptions}
        />
      </div>

      {error && (
        <div ref={errorRef} style={{
          padding: "10px 14px",
          borderRadius: "8px",
          background: "rgb(255 60 31 / 51%)",
          color: "#fff",
          fontSize: "0.875rem",
          fontFamily: "'Raleway', sans-serif",
          marginBottom: "16px",
        }}>
          {error}
        </div>
      )}
      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <Actions>
          <BackBtn onClick={() => dispatch(setCheckoutStep("sub-review"))} disabled={paying || finalizing}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><polyline points="12 19 5 12 12 5" />
            </svg>
            {" "}Back
          </BackBtn>
          <PrimaryBtn
            onClick={handleSubmit}
            disabled={paying || finalizing || !ready || !paymentComplete}
            $dimmed={!paymentComplete || vatCompanyMismatch}
            $busy={paying || finalizing}
            style={vatCompanyMismatch ? { cursor: "not-allowed" } : undefined}
          >
            {(paying || finalizing) ? (
              <>
                <Spinner />
                {finalizing ? "Finalizing..." : "Processing..."}
              </>
            ) : (
              <ButtonLabel glyph="dollar" iconFirst muted={!ready || !paymentComplete || vatCompanyMismatch}>
                {isReactivationMode ? "Reactivate" : "Subscribe"} (${(isAnnual ? Math.round(monthlyTotal * 12 * 100) / 100 : monthlyTotal).toFixed(2)}/{isAnnual ? "year" : "mo"})
              </ButtonLabel>
            )}
          </PrimaryBtn>
        </Actions>
      </SlidePanelFooter>
    </>
  );
};

const SubPaymentView: React.FC = () => {
  const { subscription } = useAppSelector((state) => state.cart);
  const subConfig = useSubscriptionConfig();

  const isAnnual = subscription.billingPeriod === "annual";
  const pricing = computeSubscriptionPricing(subConfig, { channels: subscription.channels, claims: subscription.claims, isAnnual });
  const premiumBase = pricing.baseMonthly;
  const extraChannels = pricing.extraChannels;
  const extraClaimPacks = pricing.extraClaimPacks;
  const addonsPrice = pricing.addonsMonthly; // claims billed per channel
  const monthlyTotal = premiumBase + addonsPrice;

  // Stripe needs a positive INTEGER in cents. For annual the upfront charge is
  // the annual total (× 12); round to avoid float dust (e.g. 16.15 × 100 =
  // 1614.9999…), which Stripe rejects.
  const amountCents = Math.round((isAnnual ? monthlyTotal * 12 : monthlyTotal) * 100);

  return (
    <Elements
      stripe={getStripe()}
      options={{
        mode: "subscription" as any,
        amount: amountCents,
        currency: "usd",
        // Must match the server-side subscriptions.create payment_settings.
        // payment_method_types — Stripe rejects confirmation if Elements
        // collected via automatic_payment_methods while the intent locks
        // the list.
        paymentMethodTypes: ["card", "link"] as any,
        appearance: {
          theme: "flat",
          variables: {
            colorPrimary: "#ebc28e",
            colorBackground: "#513a28",
            colorText: "#ffffff",
            colorTextSecondary: "rgba(251, 244, 236, 0.5)",
            colorTextPlaceholder: "rgba(255, 255, 255, 0.7)",
            colorDanger: "#e47a6a",
            colorIcon: "#ebc28e",
            colorIconHover: "#f7ede6",
            fontFamily: "'Raleway', sans-serif",
            fontSizeBase: "0.9375rem",
            borderRadius: "8px",
            spacingUnit: "4px",
            focusBoxShadow: "none",
            focusOutline: "none",
          },
          rules: {
            ".Input": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              padding: "14px 16px",
              boxShadow: "none",
              transition: "border-color 0.2s ease",
            },
            ".Input:focus": {
              border: "1px solid rgba(235, 194, 142, 0.5)",
              boxShadow: "none",
              outline: "none",
            },
            ".Input::placeholder": { color: "rgba(255, 255, 255, 0.8)" },
            ".Input--invalid": { border: "1px solid #e47a6a", boxShadow: "none" },
            ".Label": { color: "rgba(251, 244, 236, 0.7)", fontSize: "0.8125rem", fontWeight: "500", marginBottom: "6px" },
            ".Tab": { backgroundColor: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.15)", color: "rgba(251, 244, 236, 0.6)", boxShadow: "none" },
            ".Tab:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(235, 194, 142, 0.3)", color: "#fbf4ec" },
            ".Tab--selected": { backgroundColor: "rgba(235, 194, 142, 0.1)", border: "1px solid rgba(235, 194, 142, 0.4)", color: "#ebc28e", boxShadow: "none" },
            ".Tab--selected:hover": { backgroundColor: "rgba(235, 194, 142, 0.12)", color: "#ebc28e" },
            ".TabIcon": { fill: "rgba(251, 244, 236, 0.5)" },
            ".TabIcon--selected": { fill: "#ebc28e" },
            ".TabLabel": { color: "inherit" },
            ".Block": { backgroundColor: "transparent", border: "1px solid rgba(255, 255, 255, 0.15)", boxShadow: "none" },
            ".DropdownItem": { backgroundColor: "rgba(81, 58, 40, 0.95)", color: "#fbf4ec" },
            ".DropdownItem--highlight": { backgroundColor: "rgba(235, 194, 142, 0.15)" },
          },
        },
      }}
    >
      <SubPaymentForm />
    </Elements>
  );
};

export default SubPaymentView;
